export const isNotNullable = <T>(item: T | undefined | null): item is T => {
  return !!item;
};

export const isOfType = <T>(value: unknown, property: keyof T): value is T => (value as T)[property] !== undefined;

/**
 * This helper function allows you to create exhaustive switch constructs, such that
 * all possible values of a variable are accounted for.
 *
 * For example, if you have some variable `foo` whose type is `'foo' | 'bar'`, you
 * might like to make it impossible that more type variants are added, without your
 * switch being updated.
 *
 * ```typescript
 * switch (foo) {
 *   case 'foo': {
 *     return doSomethingWithFoo(foo);
 *   }
 *   case 'bar': {
 *     return doSomethingWithBar(foo);
 *   }
 * }
 * ```
 *
 * If someone adds 'baz' to the union type that `foo` belongs to, the above switch statement
 * is no longer exhaustive, and the typescript compiler will happily let you do this, unless you
 * happen to be in a context where `undefined` is allowed to be returned.
 *
 * If you'd like to make the above switch exhaustive, simply add a default clause that calls this
 * helper function. You can even return it, to tell the typescript compiler that your function
 * is indeed exhaustive:
 *
 * ```typescript
 * switch (foo) {
 *   case 'foo': {
 *     return doSomethingWithFoo(foo);
 *   }
 *   case 'bar': {
 *     return doSomethingWithBar(foo);
 *   }
 *   default: {
 *     return throwIfNotExhaustive<Something>(foo);
 *   }
 * }
 * ```
 *
 * Note: This function is not restricted to switch statements. You can also use this if you have a
 * chain of if statements that exhaustively check the variants of a type in a similar fashion:
 *
 * ```typescript
 * if (foo === 'foo') {
 *   return doSomethingWithFoo(foo);
 * }
 * if (foo === 'bar') {
 *   return doSomethingWithBar(foo);
 * }
 * return throwIfNotExhaustive<Something>(foo);
 * ```
 *
 * @param item The variable that should always be of type never.
 * @param msg The message that is thrown as part of the runtime check.
 *
 */
export function throwIfNotExhaustive<T>(item: never, msg = 'Exhaustive check failed'): T {
  console.error(msg);
  console.error('Expected a `never` but got:', item);
  throw new Error(msg);
}

export type PickPartial<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>> & Partial<Pick<T, K>>;

export type OmitPartial<T, K extends keyof T> = Partial<T> & Pick<T, K>;

type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`;

export type DotNestedKeys<T> = (
  T extends Record<string, unknown>
    ? {
        [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}`;
      }[Exclude<keyof T, symbol>]
    : ''
) extends infer D
  ? Extract<D, string>
  : never;
